import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CircleNotch, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h1 className="font-heading font-black text-3xl uppercase tracking-tighter mt-6 mb-3 first:mt-0" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mt-6 mb-2 first:mt-0" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-heading font-bold text-lg uppercase tracking-tight mt-4 mb-2" {...p} />,
  p: ({ node, ...p }) => <p className="text-[15px] leading-relaxed text-foreground/90 mb-3" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 space-y-1.5 mb-4 text-[15px] text-foreground/90" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 space-y-1.5 mb-4 text-[15px] text-foreground/90" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold text-white" {...p} />,
  em: ({ node, ...p }) => <em className="italic text-muted-foreground" {...p} />,
  a: ({ node, ...p }) => <a className="text-primary underline" target="_blank" rel="noreferrer" {...p} />,
  hr: () => <hr className="border-border my-5" />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-primary pl-4 italic text-muted-foreground my-3" {...p} />,
};

export default function Rules() {
  const { isAdmin } = useAuth();
  const [content, setContent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/rules");
    setContent(data.content || "");
  };

  useEffect(() => { load(); }, []);

  const startEdit = () => { setDraft(content); setEditing(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/rules", { content: draft });
      setContent(data.content);
      setEditing(false);
      toast.success("Rules updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (content === null) {
    return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter">Rules</h1>
        {isAdmin && !editing && (
          <Button onClick={startEdit} data-testid="edit-rules-button" className="h-10 bg-secondary hover:bg-[#1E1E1E] border border-border text-white font-bold uppercase text-xs tracking-widest gap-1.5 shrink-0">
            <PencilSimple size={16} weight="bold" /> Edit
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Formatting: <b className="text-white">## Heading</b>, <b className="text-white">- bullet</b>, <b className="text-white">**bold**</b>, <b className="text-white">*italic*</b>.
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={18}
            data-testid="rules-editor"
            className="w-full bg-card border border-border rounded-sm px-4 py-3 text-[15px] leading-relaxed font-mono focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Write the tournament rules…"
          />
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} data-testid="save-rules-button" className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest gap-1.5">
              <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save Rules"}
            </Button>
            <Button onClick={() => setEditing(false)} variant="outline" data-testid="cancel-rules-button" className="border-border bg-transparent hover:bg-secondary text-white font-bold uppercase tracking-widest gap-1.5">
              <X size={18} weight="bold" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-sm p-5 sm:p-6" data-testid="rules-content">
          {content.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
          ) : (
            <p className="text-muted-foreground">No rules have been added yet.{isAdmin ? " Tap Edit to add them." : ""}</p>
          )}
        </div>
      )}
    </div>
  );
}
